import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook personalizado para manejar el timeout por inactividad del usuario
 *
 * @param {Object} params - Parámetros de configuración
 * @param {Function} params.onTimeout - Callback a ejecutar cuando expire el tiempo
 * @param {number} params.timeoutDuration - Milisegundos de inactividad (default: 120000 = 2 minutos)
 * @param {number} params.warningDuration - Milisegundos antes de mostrar warning (default: 60000 = 60 segundos)
 * @param {boolean} params.enabled - Activar/desactivar el hook (default: true)
 *
 * @returns {Object} - { showWarning, remainingSeconds, resetTimer }
 */
export function useInactivityTimeout({
  onTimeout,
  timeoutDuration = 120000, // 2 minutos
  warningDuration = 60000,  // 60 segundos (1 minuto)
  enabled = true
}) {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(60);

  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  const resetTimer = useCallback(() => {
    // Limpiar timers existentes
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

    // Ocultar warning si estaba visible
    setShowWarning(false);

    if (!enabled) return;

    // Timer de warning (1:00 minuto)
    warningTimeoutRef.current = setTimeout(() => {
      setShowWarning(true);
      setRemainingSeconds(60);

      // Countdown de segundos restantes
      countdownIntervalRef.current = setInterval(() => {
        setRemainingSeconds(prev => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, timeoutDuration - warningDuration);

    // Timer principal (2 minutos)
    timeoutRef.current = setTimeout(() => {
      onTimeout();
    }, timeoutDuration);
  }, [enabled, onTimeout, timeoutDuration, warningDuration]);

  useEffect(() => {
    if (!enabled) return;

    // Event listener para actividad del usuario
    const handleActivity = () => {
      resetTimer();
    };

    // Escuchar solo 'mousemove' según requisitos
    document.addEventListener('mousemove', handleActivity);

    // Iniciar timer al montar
    resetTimer();

    // Cleanup
    return () => {
      document.removeEventListener('mousemove', handleActivity);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [enabled, resetTimer]);

  return { showWarning, remainingSeconds, resetTimer };
}
